import React, { useState, useEffect, useCallback } from 'react';
import { invoiceApi, tenantApi, formatApiErrorDetail } from '../../lib/api';
import { formatCurrency, formatDate, getStatusColor } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { AutoSuggest } from '../../components/AutoSuggest';
import { Pagination } from '../../components/Pagination';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Receipt,
  Plus,
  Eye,
  Loader2,
  CalendarIcon,
  Download,
  CreditCard,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';

// Apply the autoTable plugin to jsPDF
applyPlugin(jsPDF);

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState({ total: 0, pages: 0 });

  const [generateForm, setGenerateForm] = useState({
    tenant_id: '',
    billing_period_start: null,
    billing_period_end: null,
    cgst_rate: 9,
    sgst_rate: 9,
    notes: '',
    status: 'GENERATED',
  });
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [paymentForm, setPaymentForm] = useState({
    amount_paid: 0,
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: 'BANK_TRANSFER',
    transaction_reference: '',
    notes: '',
  });

  const fetchData = useCallback(async () => {
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (tenantFilter !== 'all') params.tenant_id = tenantFilter;
      const [invoicesRes, tenantsRes] = await Promise.all([
        invoiceApi.list(params),
        tenantApi.list({ status: 'ACTIVE', limit: 100 }),
      ]);
      const invData = invoicesRes.data;
      setInvoices(invData.items || invData);
      setPaginationData({ total: invData.total || 0, pages: invData.pages || 0 });
      const tData = tenantsRes.data;
      setTenants(tData.items || tData);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, tenantFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch uninvoiced sales count when tenant and dates change
  useEffect(() => {
    const { tenant_id, billing_period_start, billing_period_end } = generateForm;
    if (tenant_id && billing_period_start && billing_period_end) {
      setPreviewLoading(true);
      invoiceApi.previewCount({
        tenant_id,
        billing_period_start: format(billing_period_start, 'yyyy-MM-dd'),
        billing_period_end: format(billing_period_end, 'yyyy-MM-dd'),
      }).then(({ data }) => {
        setPreviewCount(data.uninvoiced_count);
      }).catch(() => {
        setPreviewCount(null);
      }).finally(() => setPreviewLoading(false));
    } else {
      setPreviewCount(null);
    }
  }, [generateForm.tenant_id, generateForm.billing_period_start, generateForm.billing_period_end]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!generateForm.billing_period_start || !generateForm.billing_period_end) {
      toast.error('Please select billing period dates');
      return;
    }

    setGenerating(true);
    try {
      await invoiceApi.generate({
        tenant_id: generateForm.tenant_id,
        billing_period_start: format(generateForm.billing_period_start, 'yyyy-MM-dd'),
        billing_period_end: format(generateForm.billing_period_end, 'yyyy-MM-dd'),
        cgst_rate: generateForm.cgst_rate,
        sgst_rate: generateForm.sgst_rate,
        notes: generateForm.notes,
        status: generateForm.status,
      });
      toast.success('Invoice generated successfully');
      setShowGenerateDialog(false);
      setPreviewCount(null);
      setGenerateForm({
        tenant_id: '',
        billing_period_start: null,
        billing_period_end: null,
        cgst_rate: 9,
        sgst_rate: 9,
        notes: '',
        status: 'GENERATED',
      });
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    setRecordingPayment(true);
    try {
      await invoiceApi.recordPayment(selectedInvoice.id, paymentForm);
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      setPaymentForm({
        amount_paid: 0,
        payment_date: format(new Date(), 'yyyy-MM-dd'),
        payment_method: 'BANK_TRANSFER',
        transaction_reference: '',
        notes: '',
      });
      fetchData();
      // Refresh the selected invoice
      const { data } = await invoiceApi.get(selectedInvoice.id);
      setSelectedInvoice(data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };

  const handleDeleteDraft = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this draft invoice?')) return;
    try {
      await invoiceApi.delete(invoiceId);
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to delete invoice');
    }
  };

  const downloadPDF = (invoice) => {
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    
    // ---- HEADER BANNER ----
    doc.setFillColor(0, 93, 232);
    doc.rect(0, 0, pageW, 38, 'F');
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.text('MOBIKOO', 20, 16);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text('2228, EFC Business Center, JK Infotech,', 20, 23);
    doc.text('Hinjewadi Phase 1, Pune, Maharashtra, 411057', 20, 28);
    doc.text('GSTIN: 27ENGPA3125K1Z5 | Mobile: 8600862333', 20, 33);
    
    // Invoice # right side
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('TAX INVOICE', pageW - 20, 16, { align: 'right' });
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(invoice.invoice_number, pageW - 20, 24, { align: 'right' });
    doc.setFontSize(8);
    doc.text(`Date: ${formatDate(invoice.generated_at)}`, pageW - 20, 31, { align: 'right' });
    
    // ---- BILL TO / DETAILS ----
    doc.setTextColor(100);
    doc.setFontSize(8);
    doc.text('BILL TO', 20, 50);
    doc.setTextColor(0);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text(invoice.tenant?.company_name || 'N/A', 20, 57);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60);
    let billY = 63;
    if (invoice.tenant?.address) {
      const lines = doc.splitTextToSize(invoice.tenant.address, 75);
      doc.text(lines, 20, billY);
      billY += lines.length * 5 + 3;
    }
    if (invoice.tenant?.phone) { doc.text('Mobile: ' + invoice.tenant.phone, 20, billY); billY += 5; }
    if (invoice.tenant?.gstin) { doc.text('GSTIN: ' + invoice.tenant.gstin, 20, billY); billY += 5; }
    
    // Right side details
    doc.setTextColor(100);
    doc.setFontSize(8);
    doc.text('INVOICE DETAILS', pageW - 80, 50);
    doc.setTextColor(0);
    doc.setFontSize(9);
    const details = [
      ['Period:', `${formatDate(invoice.billing_period_start)} to ${formatDate(invoice.billing_period_end)}`],
      ['Status:', invoice.status],
    ];
    details.forEach(([label, val], i) => {
      doc.setTextColor(100);
      doc.text(label, pageW - 80, 57 + i * 6);
      doc.setTextColor(0);
      doc.setFont(undefined, 'bold');
      doc.text(val, pageW - 55, 57 + i * 6);
      doc.setFont(undefined, 'normal');
    });
    
    doc.setDrawColor(220);
    doc.line(20, 82, pageW - 20, 82);
    
    // ---- LINE ITEMS TABLE with HSN ----
    // Club same-price plans for same branch
    const clubbed = {};
    (invoice.line_items || []).forEach(item => {
      const key = `${item.branch_name}__${item.unit_price}`;
      if (clubbed[key]) {
        clubbed[key].warranty_count += item.warranty_count;
        clubbed[key].line_total += item.line_total;
        if (!clubbed[key].plan_name.includes(item.plan_name)) {
          clubbed[key].plan_name += ', ' + item.plan_name;
        }
      } else {
        clubbed[key] = { ...item };
      }
    });
    
    const tableData = Object.values(clubbed).map(item => [
      item.branch_name,
      item.plan_name,
      item.hsn_code || '999799',
      String(item.warranty_count),
      'Rs.' + Number(item.unit_price).toLocaleString('en-IN'),
      'Rs.' + Number(item.line_total).toLocaleString('en-IN'),
    ]);
    
    doc.autoTable({
      startY: 87,
      head: [['Branch', 'Description', 'HSN', 'Qty', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [0, 93, 232], textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
      bodyStyles: { fontSize: 8.5 },
      columnStyles: { 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
      margin: { left: 20, right: 20 },
    });
    
    let finalY = doc.lastAutoTable.finalY + 8;
    
    // ---- TOTALS with CGST/SGST ----
    const cgstRate = invoice.cgst_rate || 9;
    const sgstRate = invoice.sgst_rate || 9;
    const cgstAmt = invoice.cgst_amount || (invoice.subtotal * cgstRate / 100);
    const sgstAmt = invoice.sgst_amount || (invoice.subtotal * sgstRate / 100);
    const totalTax = invoice.total_tax || (cgstAmt + sgstAmt);
    const grandTotal = invoice.total_amount || (invoice.subtotal + totalTax);
    
    const totalsX = pageW - 90;
    doc.setFillColor(248, 249, 250);
    doc.roundedRect(totalsX - 5, finalY - 3, 75, 48, 3, 3, 'F');
    doc.setFontSize(9);
    
    const fmtAmt = (n) => 'Rs.' + Number(n).toLocaleString('en-IN', {maximumFractionDigits: 0});
    
    const totalsRows = [
      ['Subtotal:', fmtAmt(invoice.subtotal)],
      ['CGST (' + cgstRate + '%):', fmtAmt(cgstAmt)],
      ['SGST (' + sgstRate + '%):', fmtAmt(sgstAmt)],
      ['Total Tax:', fmtAmt(totalTax)],
    ];
    totalsRows.forEach(([label, val], i) => {
      doc.setTextColor(100);
      doc.text(label, totalsX, finalY + 4 + i * 6);
      doc.setTextColor(0);
      doc.text(val, pageW - 22, finalY + 4 + i * 6, { align: 'right' });
    });
    
    doc.setDrawColor(0, 93, 232);
    doc.line(totalsX - 3, finalY + 29, pageW - 18, finalY + 29);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 93, 232);
    doc.text('Grand Total:', totalsX, finalY + 39);
    doc.text(fmtAmt(grandTotal), pageW - 22, finalY + 39, { align: 'right' });
    
    finalY = finalY + 55;
    
    // ---- PAYMENT HISTORY ----
    if (invoice.payments?.length > 0) {
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0);
      doc.text('Payment History', 20, finalY);
      
      doc.autoTable({
        startY: finalY + 4,
        head: [['Date', 'Method', 'Amount', 'Reference']],
        body: invoice.payments.map(p => [
          formatDate(p.payment_date), p.payment_method,
          formatCurrency(p.amount_paid), p.transaction_reference || '-',
        ]),
        theme: 'plain',
        headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        margin: { left: 20, right: 20 },
      });
      finalY = doc.lastAutoTable.finalY + 8;
    }
    
    // ---- TERMS & CONDITIONS ----
    const termsY = Math.min(finalY + 5, pageH - 65);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0);
    doc.text('Terms & Conditions', 20, termsY);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(80);
    const terms = [
      'A. Resellers are responsible for sharing all customers data with Mobikoo on same day.',
      'B. Warranty plans once activated are non-cancellable and non-refundable.',
      'C. This is a computer-generated invoice and does not require a sign or stamp.',
      'D. Subject to Pune, Maharashtra jurisdiction.',
    ];
    terms.forEach((t, i) => {
      doc.text(t, 20, termsY + 5 + i * 4, { maxWidth: pageW - 40 });
    });
    
    // ---- FOOTER ----
    doc.setDrawColor(220);
    doc.line(20, pageH - 12, pageW - 20, pageH - 12);
    doc.setFontSize(7);
    doc.setTextColor(150);
    doc.text('Mobikoo | 2228, EFC Business Center, JK Infotech, Hinjewadi Phase 1, Pune 411057 | GSTIN: 27ENGPA3125K1Z5', pageW / 2, pageH - 7, { align: 'center' });
    
    doc.save(`Invoice-${invoice.invoice_number}.pdf`);
    toast.success('Invoice PDF downloaded');
  };

  const filteredInvoices = invoices;

  const getTenantName = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant?.company_name || 'Unknown';
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Invoices
            </h1>
            <p className="text-slate-600 mt-1">Generate and manage invoices</p>
          </div>
          <Button
            onClick={() => setShowGenerateDialog(true)}
            className="bg-[#005DE8] hover:bg-[#0047B3]"
            data-testid="generate-invoice-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Generate Invoice
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="GENERATED">Generated</SelectItem>
                  <SelectItem value="PAID">Paid</SelectItem>
                  <SelectItem value="PARTIAL">Partial</SelectItem>
                  <SelectItem value="OVERDUE">Overdue</SelectItem>
                </SelectContent>
              </Select>
              <Select value={tenantFilter} onValueChange={(v) => { setTenantFilter(v); setPage(1); }}>
                <SelectTrigger className="w-full sm:w-48" data-testid="tenant-filter">
                  <SelectValue placeholder="Tenant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tenants</SelectItem>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Invoices Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
              </div>
            ) : filteredInvoices.length > 0 ? (
              <>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id} className="table-row-hover">
                      <TableCell className="font-medium text-slate-900">
                        {invoice.invoice_number}
                      </TableCell>
                      <TableCell>{getTenantName(invoice.tenant_id)}</TableCell>
                      <TableCell className="text-slate-600">
                        {formatDate(invoice.billing_period_start)} - {formatDate(invoice.billing_period_end)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(invoice.total_amount)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {(() => {
                          const paid = (invoice.payments || []).reduce((s, p) => s + p.amount_paid, 0);
                          const balance = Math.max(0, Math.round((invoice.total_amount - paid) * 100) / 100);
                          return <span className={balance > 0.01 ? 'text-red-600' : 'text-emerald-600'}>{formatCurrency(balance)}</span>;
                        })()}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(invoice.status)} border`}>
                          {invoice.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {formatDate(invoice.generated_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              const { data } = await invoiceApi.get(invoice.id);
                              setSelectedInvoice(data);
                              setShowViewDialog(true);
                            }}
                            data-testid={`view-invoice-${invoice.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {invoice.status !== 'DRAFT' && invoice.status !== 'PAID' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                const { data } = await invoiceApi.get(invoice.id);
                                setSelectedInvoice(data);
                                setShowPaymentDialog(true);
                              }}
                              data-testid={`payment-invoice-${invoice.id}`}
                            >
                              <CreditCard className="h-4 w-4" />
                            </Button>
                          )}
                          {invoice.status === 'DRAFT' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDraft(invoice.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination page={page} pages={paginationData.pages} total={paginationData.total} onPageChange={setPage} />
              </>
            ) : (
              <div className="text-center py-16">
                <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No invoices found</h3>
                <p className="text-slate-500 mt-1">Generate your first invoice</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Generate Invoice Dialog */}
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Generate Invoice</DialogTitle>
              <DialogDescription>
                Select a tenant and billing period to generate an invoice.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleGenerate}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Tenant</Label>
                  <AutoSuggest
                    options={tenants.map(t => ({ label: t.company_name, value: t.id }))}
                    value={generateForm.tenant_id}
                    onChange={(value) => setGenerateForm({ ...generateForm, tenant_id: value })}
                    placeholder="Search tenant..."
                    testId="generate-tenant-select"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="generate-start-date"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {generateForm.billing_period_start
                            ? format(generateForm.billing_period_start, 'PPP')
                            : 'Pick date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={generateForm.billing_period_start}
                          onSelect={(date) => {
                            setGenerateForm({ ...generateForm, billing_period_start: date });
                            setStartDateOpen(false);
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="generate-end-date"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {generateForm.billing_period_end
                            ? format(generateForm.billing_period_end, 'PPP')
                            : 'Pick date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={generateForm.billing_period_end}
                          onSelect={(date) => {
                            setGenerateForm({ ...generateForm, billing_period_end: date });
                            setEndDateOpen(false);
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>CGST Rate (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={generateForm.cgst_rate}
                      onChange={(e) =>
                        setGenerateForm({ ...generateForm, cgst_rate: parseFloat(e.target.value) || 0 })
                      }
                      data-testid="generate-cgst-rate"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SGST Rate (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={generateForm.sgst_rate}
                      onChange={(e) =>
                        setGenerateForm({ ...generateForm, sgst_rate: parseFloat(e.target.value) || 0 })
                      }
                      data-testid="generate-sgst-rate"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={generateForm.notes}
                    onChange={(e) => setGenerateForm({ ...generateForm, notes: e.target.value })}
                    rows={2}
                    data-testid="generate-notes"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Save As</Label>
                  <Select
                    value={generateForm.status}
                    onValueChange={(value) => setGenerateForm({ ...generateForm, status: value })}
                  >
                    <SelectTrigger data-testid="generate-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DRAFT">Draft</SelectItem>
                      <SelectItem value="GENERATED">Generated (Finalized)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                {previewCount !== null && (
                  <div className="flex-1 text-sm mr-2 self-center">
                    {previewLoading ? (
                      <span className="text-slate-500">Checking...</span>
                    ) : previewCount > 0 ? (
                      <span className="text-emerald-600 font-medium">{previewCount} uninvoiced warranty sale{previewCount !== 1 ? 's' : ''} found</span>
                    ) : (
                      <span className="text-red-500 font-medium">No uninvoiced warranty sales in this period</span>
                    )}
                  </div>
                )}
                <Button type="button" variant="outline" onClick={() => setShowGenerateDialog(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#005DE8] hover:bg-[#0047B3]"
                  disabled={generating || !generateForm.tenant_id || previewCount === 0}
                  data-testid="generate-submit"
                >
                  {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Generate Invoice
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Invoice Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>
                Invoice {selectedInvoice?.invoice_number}
              </DialogTitle>
            </DialogHeader>
            {selectedInvoice && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Tenant</p>
                    <p className="font-medium">{selectedInvoice.tenant?.company_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge className={`${getStatusColor(selectedInvoice.status)} border`}>
                      {selectedInvoice.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Billing Period</p>
                    <p className="font-medium">
                      {formatDate(selectedInvoice.billing_period_start)} -{' '}
                      {formatDate(selectedInvoice.billing_period_end)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Generated</p>
                    <p className="font-medium">{formatDate(selectedInvoice.generated_at)}</p>
                  </div>
                </div>

                {/* Line Items */}
                <div className="overflow-x-auto">
                  <h4 className="font-medium mb-2">Line Items</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Branch</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>HSN</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedInvoice.line_items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.branch_name}</TableCell>
                          <TableCell>{item.plan_name}</TableCell>
                          <TableCell>{item.hsn_code || '999799'}</TableCell>
                          <TableCell className="text-right">{item.warranty_count}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.line_total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Totals */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="text-slate-600">Subtotal</span>
                    <span>{formatCurrency(selectedInvoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="text-slate-600">CGST ({selectedInvoice.cgst_rate || 9}%)</span>
                    <span>{formatCurrency(selectedInvoice.cgst_amount || selectedInvoice.subtotal * 0.09)}</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="text-slate-600">SGST ({selectedInvoice.sgst_rate || 9}%)</span>
                    <span>{formatCurrency(selectedInvoice.sgst_amount || selectedInvoice.subtotal * 0.09)}</span>
                  </div>
                  <div className="flex justify-between mb-2 text-sm">
                    <span className="text-slate-500">Total Tax</span>
                    <span className="text-slate-500">{formatCurrency(selectedInvoice.total_tax || selectedInvoice.tax_amount)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-lg border-t pt-2">
                    <span>Grand Total</span>
                    <span>{formatCurrency(selectedInvoice.total_amount)}</span>
                  </div>
                </div>

                {/* Payments */}
                {selectedInvoice.payments?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Payment History</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Reference</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedInvoice.payments.map((payment, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{formatDate(payment.payment_date)}</TableCell>
                            <TableCell>{payment.payment_method}</TableCell>
                            <TableCell className="text-right">{formatCurrency(payment.amount_paid)}</TableCell>
                            <TableCell>{payment.transaction_reference || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {selectedInvoice.notes && (
                  <div>
                    <h4 className="font-medium mb-2">Notes</h4>
                    <p className="text-slate-600">{selectedInvoice.notes}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => downloadPDF(selectedInvoice)}
                data-testid="download-pdf"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Record Payment Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Record Payment</DialogTitle>
              <DialogDescription>
                Invoice: {selectedInvoice?.invoice_number}
                <br />
                Outstanding: {formatCurrency(
                  (selectedInvoice?.total_amount || 0) -
                    (selectedInvoice?.payments?.reduce((sum, p) => sum + p.amount_paid, 0) || 0)
                )}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleRecordPayment}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Amount (INR)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={paymentForm.amount_paid}
                    onChange={(e) =>
                      setPaymentForm({ ...paymentForm, amount_paid: parseFloat(e.target.value) || 0 })
                    }
                    required
                    data-testid="payment-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Date</Label>
                  <Input
                    type="date"
                    value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                    required
                    data-testid="payment-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Select
                    value={paymentForm.payment_method}
                    onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
                  >
                    <SelectTrigger data-testid="payment-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CASH">Cash</SelectItem>
                      <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="CHEQUE">Cheque</SelectItem>
                      <SelectItem value="OTHER">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Transaction Reference</Label>
                  <Input
                    value={paymentForm.transaction_reference}
                    onChange={(e) =>
                      setPaymentForm({ ...paymentForm, transaction_reference: e.target.value })
                    }
                    placeholder="Optional"
                    data-testid="payment-reference"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#005DE8] hover:bg-[#0047B3]"
                  disabled={recordingPayment || paymentForm.amount_paid <= 0}
                  data-testid="payment-submit"
                >
                  {recordingPayment ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Record Payment
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
